import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, tap } from 'rxjs';

import { isNil, omitBy } from 'lodash';

import { SimpleLookupResult } from '@typeDefs/general/general';
import { UtilityService } from '@core/services/utility.service';
import { isEmptyValue } from '@core/helpers/value-empty-checker.helper';
import { YesNoEnum } from '@shared/model/yes-no.model';
import {
  GetFormDataParams,
  IOfficeData,
  IOfficeFormItemGroup,
  IOfficeFormMetadata,
  IOfficeGridPageSettings,
  OfficeDataItem,
  OfficeFieldsValuesObj,
} from '../models/office-data.model';
import { OfficeService } from '../services/office.service';
import { ComboBoxItem } from '../models/combobox-item.model';
import { DEFAULT_PAGE_SETTING } from '../office-form/office-grid/office-grid.constants';
import { commonCatchErrorHandler, NEW_FORM_KEY_ID } from '../office.constants';
import { IOfficeCompany } from '../models/companies.model';
import { IOfficeFormListItem } from '../models/form-list.models';
import { IOfficeMenuItem } from '../models/office-nav.model';
import { OfficeToolsEventsService } from '../office-form/events/office-tools-events.service';
import { IOfficeFormDetails } from '../models/office-form-details.model';
import { OfficeGlobalFilter } from '../office-tools-header/models/office-grid-header.model';

@Injectable({ providedIn: 'root' })
export class OfficeStore {
  // State
  readonly companyList$ = signal<IOfficeCompany[]>([]);
  readonly formList$ = signal<IOfficeFormListItem[]>([]);
  readonly selectedCompany$ = signal<number>(null);
  readonly selectedForm$ = signal<string>(null);
  readonly relatedToForm$ = signal<{ title: string; form: string }>(null);
  readonly formItemMetadata$ = signal<IOfficeFormMetadata>(null);
  readonly totalRecordsCount$ = signal<number>(null);
  readonly answers$ = signal<OfficeDataItem[]>([]);
  readonly parentForm$ = signal<string>(null);
  readonly parentFormData$ = signal<string>(null);
  readonly pageSettings$ = signal<Record<string, IOfficeGridPageSettings>>(null);
  readonly selectedGroup$ = signal<IOfficeMenuItem>(null);
  readonly globalFilters$ = signal<Record<string, Record<number, unknown>>>({});

  // Selectors
  readonly title$ = computed(() => this.formItemMetadata$()?.FormHeaderInfo.FormDesc);
  readonly currentPageSetting$ = computed(() => this.getPageSettingForSelecting());
  readonly formSecurity$ = computed(() => this.formItemMetadata$()?.HeaderSecurityData);
  readonly isReadonly$ = computed(() => this.formItemMetadata$()?.FormHeaderInfo.IsReadOnly);
  readonly comboboxMap$ = computed(() => this.createComboboxMap(this.formItemMetadata$()?.ComboBoxData || []));
  readonly statusesMap$ = computed(() => this.formItemMetadata$()?.Statuses || []);
  readonly formItemGroups$ = computed(() => this.formItemMetadata$()?.FormItemGroups);
  readonly fields$ = computed(() => this.formItemMetadata$()?.Fields || []);
  readonly fieldsMap$ = computed(() => this.fields$()?.reduce((res, field) => ({ ...res, [field.Seq]: field }), {}) || {});
  readonly fieldsGrid$ = computed(
    () =>
      // Column name filtration is to be considered a patch due to lack of knowledge of handling such records
      // TODO: Usually 999 is considered a sequence to skip - 9999 may be a bug
      // TODO: Filter column index by GridCol; GridCol is not used for the grid. It is necessary to check whether valid values are returned from the API and sort them according to the metadata.
      this.fields$()?.filter(
        field => field.VistaFormItem.ShowGrid === YesNoEnum.Y && field.VistaFormItem.ColumnName && field.Seq != 9999
      ) || []
  );
  readonly formFiltersMap$ = computed(() => {
    const searchSettings =
      this.pageSettings$() &&
      this.pageSettings$()[this.selectedForm$()] &&
      this.pageSettings$()[this.selectedForm$()].search?.map(pair => pair.split(','));
    return isNil(searchSettings) ? null : Object.fromEntries(searchSettings);
  });
  readonly fieldsFormMap$ = computed(
    () =>
      // The same as for the Grid. Column name filtration is to be considered a patch due to lack of knowledge of handling such records
      // TODO: Usually 999 is considered a sequence to skip - 9999 may be a bug
      this.fields$()?.reduce((res, field) => {
        return field.Seq != 9999 ? { ...res, [field.Seq]: field } : res;
      }, {}) || {}
  );
  readonly formGlobalFilters$ = computed(() => this.calculateGlobalFilter(this.formItemMetadata$()));

  private officeService = inject(OfficeService);
  private utilityService = inject(UtilityService);
  private officeToolsEventsService = inject(OfficeToolsEventsService);
  private commonCatchErrorHandler = commonCatchErrorHandler();

  // Load form API
  public loadData(params: Partial<GetFormDataParams>): Observable<IOfficeData> {
    const title = this.title$();
    const requestParams: GetFormDataParams = {
      ...this.getDefaultRequestParams(),
      ...(params as GetFormDataParams),
    };

    return this.handleLoad(this.officeService.getFormData(requestParams)).pipe(
      tap(data => {
        if (requestParams.includeMetadata) {
          this.formItemMetadata$.set(data.FormItemMetadata);
          this.officeToolsEventsService.formEvents.clear();
          data.FormItemMetadata.modalFormEvents?.forEach(
            this.officeToolsEventsService.formEvents.add,
            this.officeToolsEventsService.formEvents
          );
        }
        !params.keyid && this.updatePageSetting(requestParams);
        this.totalRecordsCount$.set(data.count);
        this.answers$.set(data.items);
        this.relatedToForm$.set(requestParams.parentForm ? { title, form: requestParams.parentForm } : null);
      })
    );
  }

  public loadCompany(): Observable<IOfficeCompany[]> {
    return this.handleLoad(this.officeService.getCompanies()).pipe(
      tap(companies => {
        this.companyList$.set(companies);
      })
    );
  }

  public loadFormList(): Observable<IOfficeFormListItem[]> {
    return this.handleLoad(this.officeService.getFormList(this.selectedCompany$(), null)).pipe(
      tap(formList => this.formList$.set(formList))
    );
  }

  public loadFormDetails(currentAnswer: OfficeDataItem): Observable<IOfficeFormDetails> {
    this.utilityService.openModal();
    return this.officeService
      .getFormDetails({ co: this.selectedCompany$(), form: this.selectedForm$(), formData: JSON.stringify(currentAnswer) })
      .pipe(
        tap(({ defaults }) => this.updateAnswerByKeyId(currentAnswer.KeyID, defaults)),
        finalize(() => this.utilityService.closeModal()),
        catchError(this.commonCatchErrorHandler)
      );
  }

  public checkExistingByKeyFields(keyFieldsData: OfficeFieldsValuesObj): Observable<number> {
    const filter = Object.entries(keyFieldsData).map(([key, value]) => `${key},${value}`);
    const requestParams: GetFormDataParams = {
      ...this.getDefaultRequestParams(),
      rowsPerPage: 1,
      includeMetadata: false,
      filter,
    };
    return this.handleLoad(this.officeService.getFormData(requestParams)).pipe(map(({ items }) => (items.length ? items[0].KeyID : null)));
  }

  // Helpers
  public getReportGatewayCat(): string {
    const REPORTS_GROUP_SHORT_NAME = 'RP';
    const moduleShortName = this.selectedGroup$()?.formList?.[0]?.[0]?.ModuleShortName;
    return !moduleShortName || moduleShortName === REPORTS_GROUP_SHORT_NAME ? 'All' : moduleShortName;
  }

  public getComboboxBySeq(seq: number | string): ComboBoxItem[] {
    const comboType = this.fieldsMap$()[seq]?.VistaFormItem.ComboType;
    return this.comboboxMap$()[comboType];
  }

  public getFormItemGroupById(groupID: IOfficeFormItemGroup['GroupID']): IOfficeFormItemGroup {
    return this.formItemGroups$().find(({ GroupID }) => GroupID === groupID);
  }

  public getAnswerByKeyId(keyId: OfficeDataItem['KeyID']): OfficeDataItem {
    return this.answers$()?.find(({ KeyID }) => Number(keyId) === KeyID);
  }

  public updateAnswerByKeyId(keyId: OfficeDataItem['KeyID'], partialAnswerData: OfficeFieldsValuesObj): void {
    const answerIndex = this.answers$().findIndex(({ KeyID }) => keyId === KeyID);
    this.answers$.update(state => {
      const copy = [...state];
      copy.splice(answerIndex, 1, { ...state[answerIndex], ...partialAnswerData });
      return copy;
    });
  }

  public clearUpAnswers(): void {
    if (this.getAnswerByKeyId(NEW_FORM_KEY_ID)) {
      this.answers$.update(state => state.filter(({ KeyID }) => KeyID !== NEW_FORM_KEY_ID));
    }
  }

  public getPageSettingForSelecting(): IOfficeGridPageSettings {
    const pageSettings = this.pageSettings$();
    return !isEmptyValue(pageSettings) && pageSettings[this.selectedForm$()];
  }

  private createComboboxMap(comboboxData: SimpleLookupResult[]): Record<string, ComboBoxItem[]> {
    return comboboxData?.reduce((data, val) => {
      if (!data[val.ComboType]) {
        data[val.ComboType] = [val];
      } else if (!data[val.ComboType].some(item => item.DatabaseValue === val.DatabaseValue)) {
        data[val.ComboType] = [...data[val.ComboType], val];
      }
      return data;
    }, {});
  }

  private updatePageSetting(params: Partial<GetFormDataParams>): void {
    const settings = omitBy(
      {
        orderby: params.orderby,
        pageNumber: params.pageNumber,
        rowsPerPage: params.rowsPerPage,
        search: params.search,
      },
      isNil
    );

    this.pageSettings$.update(state => ({
      ...state,
      [this.selectedForm$()]: settings,
    }));
  }

  private getDefaultRequestParams(): GetFormDataParams {
    return {
      co: this.selectedCompany$(),
      form: this.selectedForm$(),
      includeMetadata: true,
      pageNumber: DEFAULT_PAGE_SETTING.currentPage,
      rowsPerPage: DEFAULT_PAGE_SETTING.pageSize,
      parentForm: this.parentForm$(),
      parentFormData: this.parentFormData$(),
    };
  }

  private handleLoad<T>(observable$: Observable<T>): Observable<T> {
    this.utilityService.openModal();
    return observable$.pipe(
      finalize(() => this.utilityService.closeModal()),
      catchError(this.commonCatchErrorHandler)
    );
  }

  private calculateGlobalFilter(metadata: IOfficeFormMetadata): OfficeGlobalFilter {
    return (
      metadata?.Fields.filter(field => field.FilterConfiguration).reduce((acc, metadataField) => {
        acc[metadataField.Seq] = {
          field: metadataField,
          value: metadataField.DefaultValue,
          filteredField: metadata.Fields.find(field => field.Seq == metadataField.FilterConfiguration.FilteredSeq),
        };
        return acc;
      }, {} as OfficeGlobalFilter) || null
    );
  }
}
